/**
 * WarrantyReceipt — shown after a successful warranty renewal payment.
 *
 * Reuses:
 *   - Card, CardContent, CardHeader, CardTitle from existing UI components
 *   - Button, Badge, Separator from existing UI components
 *   - WarrantyInvoiceView (new, wraps existing Dialog/Card/Badge)
 *   - generateWarrantyInvoicePDF (new, same jsPDF stack as existing)
 *   - toast from existing hooks
 *
 * Does NOT contain a Track Shipment section (warranty renewal has no shipment).
 * Does NOT change any existing file.
 */
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ChevronLeft,
  Download,
  Eye,
  FileText,
  CheckCircle2,
  ShieldCheck,
  Circle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import WarrantyInvoiceView from "../components/WarrantyInvoiceView";
import { generateWarrantyInvoicePDF } from "../utils/warrantyInvoicePDF";
import { toast } from "../hooks/use-toast";

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (date) => {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const fmtShort = (date) => {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const GST_RATE = 18; // %

/**
 * Build the invoiceData object expected by WarrantyInvoiceView and generateWarrantyInvoicePDF
 * from the state passed by WarrantyCheckout.
 */
const buildInvoiceData = (state) => {
  const {
    productName,
    renewalDuration,
    renewalPrice,
    previousWarrantyEndDate,
    newWarrantyEndDate,
    orderId,
    razorpayPaymentId,
    userName,
    userEmail,
    userPhone,
    userAddress,
  } = state;

  const total = Number(renewalPrice);
  // Calculate GST using inclusive math: GST = Total * GST% / (100 + GST%)
  const gstAmount = parseFloat(((total * GST_RATE) / (100 + GST_RATE)).toFixed(2));
  const subtotal = parseFloat((total - gstAmount).toFixed(2));

  const now = new Date();
  const invoiceNumber = `REN-${now.getTime()}`;
  const renewalRefNo = `REN-${String(Math.floor(Math.random() * 9000) + 1000)}`;
  const invoiceDate = now.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return {
    invoice: {
      invoiceNumber,
      invoiceDate,
      renewalRefNo,
      originalOrderNo: orderId || "—",
      paymentMethod: "ONLINE",
      paymentStatus: "PAID",
      transactionType: "WARRANTY RENEWAL",
      customer: {
        name: userName || "Customer",
        email: userEmail || "",
        phone: userPhone || "",
        address: userAddress || null,
      },
      items: [
        {
          srNo: 1,
          description: productName,
          warranty: `${renewalDuration} Month Extension`,
          quantity: 1,
          unitPrice: subtotal,
          lineTotal: subtotal,
        },
      ],
      pricing: { subtotal, gstPercentage: GST_RATE, gstAmount, total },
    },
    order: {
      _id: orderId || "—",
      orderType: "WARRANTY RENEWAL",
      paymentStatus: "PAID",
    },
  };
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function WarrantyReceipt() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [showInvoice, setShowInvoice] = useState(false);

  // ── Guard ─────────────────────────────────────────────────────────────────
  if (!state?.productName) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <ShieldCheck className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">No receipt data found.</p>
          <Button
            onClick={() => navigate("/profile")}
            variant="outline"
            className="mt-4"
          >
            <ChevronLeft className="w-4 h-4 mr-2" /> Back to Profile
          </Button>
        </div>
      </div>
    );
  }

  const {
    productName,
    renewalDuration,
    renewalPrice,
    previousWarrantyEndDate,
    newWarrantyEndDate,
    razorpayPaymentId,
  } = state;

  const invoiceData = buildInvoiceData(state);
  const total = Number(renewalPrice);

  const handleViewInvoice = () => setShowInvoice(true);

  const handleDownloadInvoice = () => {
    generateWarrantyInvoicePDF(invoiceData);
    toast({
      title: "Invoice Downloaded",
      description: "Your warranty invoice has been downloaded.",
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="py-12 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4">
        {/* Back button — same style as OrderDetail */}
        <Button
          variant="ghost"
          onClick={() => navigate("/profile")}
          className="mb-6"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to My Products
        </Button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* ── Left column (main content) ─────────────────────────── */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header card — mirrors OrderDetail header card */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h1 className="text-2xl font-bold mb-1">{productName}</h1>
                    <p className="text-gray-600">
                      Renewed on {fmt(new Date())}
                    </p>
                  </div>
                  <Badge className="bg-green-500 text-white text-base py-1 px-4">
                    PAID
                  </Badge>
                </div>

                {/* View Invoice / Download PDF buttons — same as OrderDetail */}
                <div className="flex space-x-4">
                  <Button
                    variant="outline"
                    onClick={handleViewInvoice}
                    className="flex-1"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Invoice
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDownloadInvoice}
                    className="flex-1"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                </div>

                {/* Invoice number banner — same green style as OrderDetail */}
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center">
                    <FileText className="w-5 h-5 text-green-600 mr-2" />
                    <div>
                      <p className="text-sm font-semibold text-green-800">
                        Invoice Generated
                      </p>
                      <p className="text-sm text-green-700">
                        Invoice Number: {invoiceData.invoice.invoiceNumber}
                      </p>
                      <p className="text-xs text-green-600 mt-1">
                        Generated on: {invoiceData.invoice.invoiceDate}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Success + Renewal Details card (replaces Track Shipment — this is warranty, no shipment) */}
            <Card>
              <CardContent className="p-6">
                {/* Success banner */}
                <div className="flex items-center gap-3 mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle2 className="w-8 h-8 text-green-600 flex-shrink-0" />
                  <div>
                    <p className="font-bold text-green-800 text-base">
                      Warranty Renewed Successfully!
                    </p>
                    <p className="text-green-700 text-sm mt-0.5">
                      Your warranty has been extended. See details below.
                    </p>
                  </div>
                </div>

                <h2 className="text-xl font-bold mb-4">Renewal Details</h2>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500 font-medium">
                      Renewal Plan
                    </span>
                    <span className="font-bold">
                      {renewalDuration} Month Extension
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 font-medium">
                      Previous Expiry
                    </span>
                    <span className="font-bold text-gray-400 line-through">
                      {fmt(previousWarrantyEndDate)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 font-medium">
                      New Expiry
                    </span>
                    <span className="font-black text-green-700 text-base">
                      {fmt(newWarrantyEndDate)}
                    </span>
                  </div>
                  {razorpayPaymentId && (
                    <>
                      <Separator />
                      <div className="flex justify-between">
                        <span className="text-gray-500 font-medium">
                          Payment ID
                        </span>
                        <span className="font-mono text-xs text-gray-600 break-all text-right max-w-[60%]">
                          {razorpayPaymentId}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ── Right column (order summary) — same as OrderDetail ───── */}
          <div className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-6">Renewal Summary</h2>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal</span>
                    <span className="font-semibold">
                      ₹
                      {invoiceData.invoice.pricing.subtotal.toLocaleString(
                        "en-IN",
                        { minimumFractionDigits: 2 },
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>GST ({GST_RATE}%)</span>
                    <span className="font-semibold">
                      ₹
                      {invoiceData.invoice.pricing.gstAmount.toLocaleString(
                        "en-IN",
                        { minimumFractionDigits: 2 },
                      )}
                    </span>
                  </div>
                </div>
                <Separator className="my-4" />
                <div className="flex justify-between text-lg font-bold mb-6">
                  <span>Total</span>
                  <span>₹{total.toLocaleString("en-IN")}</span>
                </div>

                <Separator className="my-4" />

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment Method</span>
                    <span className="font-semibold">ONLINE</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment Status</span>
                    <Badge className="bg-green-500 text-white text-xs">
                      PAID
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Transaction Type</span>
                    <Badge className="bg-blue-600 text-white text-xs">
                      WARRANTY RENEWAL
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Warranty Invoice dialog (reuses existing Dialog/Card/Badge via WarrantyInvoiceView) */}
      <WarrantyInvoiceView
        invoiceData={invoiceData}
        isOpen={showInvoice}
        onClose={() => setShowInvoice(false)}
      />
    </div>
  );
}
